import { Token } from '@uniswap/sdk-core'
import { computePoolAddress } from '@uniswap/v3-sdk'
import { useWeb3React } from '@web3-react/core'
import { useSingleContractMultipleData } from 'lib/hooks/multicall'
import { useMemo } from 'react'
import { addHexPrefix } from 'utils/addHexPrefix'

import { V3_CORE_FACTORY_ADDRESSES } from '../constants/addresses'
import { PositionDetails } from '../types/position'
import { useV3StakerContract } from './useContract'
import { useIncentiveConfig } from './useIncentive'
import { useV3Positions } from './useV3Positions'
import { useV3StakerPositionsFromUser } from './useV3StakerPositionsFromUser'
interface UseIncentivePositionsResults {
  loading: boolean
  positions: PositionDetails[] | undefined
}

/**
 * Returns positions that are related to a specific incentive by matching their tokens with the incentive's pool
 * Includes both positions owned directly by the user and positions deposited in the V3 staker contract
 * @param incentiveId The ID of the incentive to filter positions for
 * @returns Filtered positions related to the incentive
 */
export function useIncentivePositions(incentiveId: string | undefined): UseIncentivePositionsResults {
  const { account, chainId } = useWeb3React()
  const v3StakerContract = useV3StakerContract()

  const incentive = useIncentiveConfig(incentiveId)

  // Get positions owned directly by the user
  const { positions: directPositions, loading: directPositionsLoading } = useV3Positions(account)

  // Get positions deposited by the user in the V3 staker contract
  const { positions: stakerPositions, loading: stakerPositionsLoading } = useV3StakerPositionsFromUser()

  const stakesArgs = useMemo(() => {
    if (stakerPositions?.length && incentiveId) {
      return stakerPositions.map((pos) => [pos.tokenId.toString(), addHexPrefix(incentiveId)])
    }
    return []
  }, [stakerPositions, incentiveId])

  const stakesResult = useSingleContractMultipleData(v3StakerContract, 'stakes', stakesArgs)
  const stakes = stakesResult.map(({ result }) => {
    return {
      liquidity: result?.liquidity,
      secondsPerLiquidityInsideInitialX128: result?.secondsPerLiquidityInsideInitialX128,
    }
  })
  const stakesLoading = useMemo(() => stakesResult.some(({ loading }) => loading), [stakesResult])

  const stakerPositionsWithInfo = useMemo(() => {
    return stakerPositions?.map((pos, index) => {
      const position = {
        ...pos,
      }

      position.stakingInfo = {
        ...pos.stakingInfo,
        ...stakes[index],
      }

      return position
    })
  }, [stakerPositions, stakes])

  // Filter positions that match the incentive's pool
  const filteredPositions = useMemo(() => {
    const factoryAddress = chainId ? V3_CORE_FACTORY_ADDRESSES[chainId] : undefined

    if ((!directPositions && !stakerPositionsWithInfo) || !incentive || !chainId || !factoryAddress) return []

    // The incentive contains the pool address directly, but we verify it matches our tokens
    const incentivePoolAddress = incentive.key.pool

    // Combine and process both direct and staker positions
    const allPositions = [...(stakerPositionsWithInfo ?? []), ...(directPositions ?? [])]

    // get staking info for each nft

    return allPositions.filter((position) => {
      // Create a Token instance for each token in the position (decimals don't matter for pool address computation)
      const tokenA = new Token(chainId, position.token0, 1)
      const tokenB = new Token(chainId, position.token1, 1)

      // Compute the pool address for this position
      const computedPoolAddress = computePoolAddress({
        factoryAddress,
        tokenA,
        tokenB,
        fee: position.fee,
      }).toLowerCase()

      // Check if the computed pool address matches the incentive's pool address
      return computedPoolAddress === incentivePoolAddress.toLowerCase()
    })
  }, [directPositions, stakerPositionsWithInfo, incentive, chainId])

  return {
    loading: directPositionsLoading || stakerPositionsLoading || stakesLoading,
    positions: filteredPositions,
  }
}
